import React, { useState } from "react";
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  Menu,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import EllipsisDropdown from "../../../../../components/shared-components/EllipsisDropdown";
import Flex from "../../../../../components/shared-components/Flex";
import { useTranslation } from "react-i18next";
import { useWhatsappTemplates } from "../../../../../data/hooks/firestore/useWhatsappTemplates";
import Loading from "../../../../../components/shared-components/Loading";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const LANGUAGE_OPTIONS = [
  { value: "nl", label: "Nederlands" },
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
];

const WhatsappTemplates = () => {
  const { t } = useTranslation();
  const { data: templates, loading, error, addTemplate, updateTemplate, deleteTemplate } = useWhatsappTemplates();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditingTemplate(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      id: template.id,
      name: template.name,
      language: template.language,
      body: template.body,
      variables: template.variables || [],
    });
    setIsModalVisible(true);
  };

  const handleView = (template) => {
    setViewingTemplate(template);
    setIsViewModalVisible(true);
  };

  const handleDelete = async (templateId) => {
    await deleteTemplate(templateId);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, values);
      } else {
        await addTemplate(values);
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      console.error("Form validation failed:", err);
    }
  };

  const dropdownMenu = (record) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => handleEdit(record)}>
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>Edit</span>
          </Flex>
        </a>
      </Menu.Item>
      <Menu.Item>
        <Popconfirm
          placement="bottom"
          title="Are you sure you want to delete this template?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <a onClick={(e) => e.preventDefault()}>
            <DeleteOutlined />
            <span style={{ marginLeft: 4 }}>Delete</span>
          </a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 280,
      render: (text, record) => (
        // <a onClick={() => handleView(record)} style={{ color: "#1890ff" }}>
        //   {text}
        // </a>
        <button className={"text-button"} onClick={() => handleView(record)}>
          {text}
        </button>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 260,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 100,
      render: (lang) => <Tag>{lang?.toUpperCase()}</Tag>,
    },
    {
      title: "Body Preview",
      dataIndex: "body",
      key: "body",
      ellipsis: true,
      render: (text) => text?.substring(0, 200) + (text?.length > 80 ? "..." : ""),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, record) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  if (loading) {
    return <Loading cover="content" />;
  }

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <p style={{ color: "red" }}>Error loading templates: {error.message}</p>
          <Button onClick={() => window.location.reload()}>Reload Page</Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1>WhatsApp Templates</h1>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Template
          </Button>
        </div>

        <Table columns={columns} dataSource={templates} rowKey="id" pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        title={
          <>
            <EyeOutlined style={{ marginRight: 8 }} />
            View Template
          </>
        }
        open={isViewModalVisible}
        onCancel={() => {
          setIsViewModalVisible(false);
          setViewingTemplate(null);
        }}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setIsViewModalVisible(false);
              handleEdit(viewingTemplate);
            }}
          >
            Edit
          </Button>,
        ]}
        width={800}
      >
        {viewingTemplate && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Template ID:</Text>
              <div style={{ marginTop: 4, padding: "8px 12px", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                {viewingTemplate.id}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Template Name:</Text>
              <div style={{ marginTop: 4, padding: "8px 12px", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                {viewingTemplate.name}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Language:</Text>
              <div style={{ marginTop: 4 }}>
                <Tag>{viewingTemplate.language?.toUpperCase()}</Tag>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong>Message Body:</Text>
              <div
                style={{
                  marginTop: 4,
                  padding: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                }}
              >
                {viewingTemplate.body}
              </div>
            </div>

            {viewingTemplate.variables && viewingTemplate.variables.length > 0 && (
              <div>
                <Divider>Variables</Divider>
                <div style={{ backgroundColor: "#f5f5f5", padding: "12px", borderRadius: 4 }}>
                  {viewingTemplate.variables.map((variable, index) => (
                    <div key={index} style={{ marginBottom: 8, display: "flex", gap: 16 }}>
                      <div>
                        <Text strong>Key:</Text> <Text>{variable.key}</Text>
                      </div>
                      <div>
                        <Text strong>Label:</Text> <Text>{variable.label}</Text>
                      </div>
                      <div>
                        <Text strong>Placeholder:</Text> <Text type="secondary">{variable.placeholder}</Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={editingTemplate ? "Edit Template" : "Add Template"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        okText={editingTemplate ? "Update" : "Create"}
        cancelText="Cancel"
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" label="Template ID" rules={[{ required: true, message: "Please enter template ID" }]}>
            <Input disabled={!!editingTemplate} placeholder="e.g., template_1_nl" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: "Please enter template name" }]}
          >
            <Input placeholder="e.g., Proefpakket bevestiging" />
          </Form.Item>

          <Form.Item name="language" label="Language" rules={[{ required: true, message: "Please select a language" }]}>
            <Select placeholder="Select language">
              {LANGUAGE_OPTIONS.map((lang) => (
                <Option key={lang.value} value={lang.value}>
                  {lang.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="body"
            label="Message Body"
            rules={[{ required: true, message: "Please enter message body" }]}
            extra="Use {{1}}, {{2}}, etc. for variables"
          >
            <TextArea rows={6} placeholder="Hi {{1}}! Dank voor het aanvragen van een proefpakket..." />
          </Form.Item>

          <Divider>Variables</Divider>

          <Form.List name="variables">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, "key"]}
                      rules={[{ required: true, message: "Missing key" }]}
                      style={{ marginBottom: 0, width: 80 }}
                    >
                      <Input placeholder="1" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "label"]}
                      rules={[{ required: true, message: "Missing label" }]}
                      style={{ marginBottom: 0, width: 200 }}
                    >
                      <Input placeholder="Naam" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "placeholder"]}
                      rules={[{ required: true, message: "Missing placeholder" }]}
                      style={{ marginBottom: 0, width: 200 }}
                    >
                      <Input placeholder="Jan" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} style={{ color: "red" }} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Variable
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsappTemplates;
